import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Sparkles, Send, Loader2, Copy, ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = {
  final_wishes: [
    "Help me describe the tone I'd like for my memorial service.",
    "Draft gentle obituary notes about my life.",
    "Suggest meaningful readings and music.",
  ],
  legacy: [
    "Help me write a loving letter to my children.",
    "Help me put my life values into words.",
    "Draft an opening for sharing my life story.",
  ],
  general: [
    "What should I remember to include in this section?",
    "Help me phrase this more clearly.",
  ],
};

export default function AIAssistant({ open, onOpenChange, mode = "general", sessionId, context = "", fieldLabel, onInsert }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) setMessages([]);
  }, [open, mode]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const prompt = (text ?? input).trim();
    if (!prompt || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: prompt }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai/assist`, {
        session_id: sessionId,
        prompt,
        context,
        mode,
      });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      toast.error("The assistant is unavailable right now. Please try again.");
      setMessages((m) => [...m, { role: "assistant", text: "I'm sorry, I couldn't respond just now. Please try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-ivory p-0 flex flex-col" data-testid="ai-assistant-panel">
        <SheetHeader className="bg-sage/90 text-white px-6 py-5">
          <SheetTitle className="flex items-center gap-2 text-white font-serif text-2xl">
            <Sparkles className="w-5 h-5" strokeWidth={1.5} />
            Compassionate Assistant
          </SheetTitle>
          <p className="text-white/85 text-sm">
            {fieldLabel ? `Helping with: ${fieldLabel}` : "Here to help you find the right words."}
          </p>
          <SheetDescription className="sr-only">
            A compassionate AI assistant to help you write your legacy and final wishes.
          </SheetDescription>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto planner-scroll px-5 py-6 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-charcoal/70">Try asking:</p>
              {(SUGGESTIONS[mode] || SUGGESTIONS.general).map((s, i) => (
                <button key={i} onClick={() => send(s)} data-testid={`ai-suggestion-${i}`}
                  className="block w-full text-left text-sm bg-white border border-gold/25 rounded-md px-4 py-3 text-navy hover:border-gold/60 hover:bg-gold/5 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div className={`max-w-[85%] rounded-md px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-navy text-ivory" : "bg-white border border-gold/25 text-charcoal"
              }`} data-testid={`ai-message-${m.role}-${i}`}>
                {m.text}
                {m.role === "assistant" && (
                  <div className="flex gap-3 mt-3 pt-2 border-t border-gold/15">
                    <button onClick={() => { navigator.clipboard.writeText(m.text); toast.success("Copied"); }}
                      className="flex items-center gap-1 text-xs text-sage hover:text-navy transition-colors" data-testid={`ai-copy-${i}`}>
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    {onInsert && (
                      <button onClick={() => { onInsert(m.text); toast.success("Inserted into worksheet"); onOpenChange(false); }}
                        className="flex items-center gap-1 text-xs text-sage hover:text-navy transition-colors" data-testid={`ai-insert-${i}`}>
                        <ArrowDownToLine className="w-3 h-3" /> Insert
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex items-center gap-2 text-sage text-sm" data-testid="ai-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Thinking with care…
            </div>
          )}
        </div>

        <div className="border-t border-gold/20 p-4 bg-white">
          <div className="flex gap-2">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask for help writing…" rows={2} data-testid="ai-input"
              className="resize-none bg-ivory border-gold/25 focus-visible:ring-sage" />
            <Button onClick={() => send()} disabled={loading} data-testid="ai-send-btn"
              className="bg-gold hover:bg-gold/90 text-navy self-end h-10 w-10 p-0 rounded-full">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
